import React from 'react';
import { MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { CRYPTOLOGY_CONTACT } from '../../config/cryptologyContact';
import { openSupportChat } from '@crm/frontend-core/support/openSupportChat';
import { segmentMarketingRoutes } from '../../config/marketingRoutes';
import { BrandLogo } from '../brand/BrandLogo';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { cn } from '../../lib/utils';

const SALES_EMAIL = 'sales@inmueble.red';
const COLUMN_TITLE_CLASS =
  'mb-3 font-sans text-xs font-bold uppercase tracking-[0.14em] text-white/70';
const COLUMN_LINK_CLASS =
  'block py-2.5 text-xs font-bold uppercase tracking-widest text-white/75 transition-colors hover:text-white';
const LEGAL_TERM_CLASS =
  'text-xs font-semibold uppercase tracking-[0.12em] text-white/65';
const LEGAL_VALUE_CLASS = 'mt-1 text-sm leading-6 text-white/90';

export function Footer(): React.JSX.Element {
  const { t } = useTranslation(['common', 'segments']);
  const isHomePage = useLocation().pathname === '/';

  return (
    <footer
      className={cn(
        'py-16 text-white md:py-20',
        isHomePage ? 'bg-[var(--ir-deep)]' : 'bg-brand-primary'
      )}
    >
      <div className="section-padding">
        <div className="mb-12 grid grid-cols-2 gap-x-8 gap-y-10 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
          <div className="col-span-2 lg:col-span-1">
            <div className="mb-6">
              <BrandLogo
                reversed
                wordmarkVariant={isHomePage ? 'editorial' : 'app'}
                linkTo="/"
              />
            </div>
            <p className="mb-5 max-w-xs font-sans text-sm text-white/70">
              {t('brand.tagline')}
            </p>
            <a
              href={`mailto:${SALES_EMAIL}`}
              className="inline-block py-2 font-mono text-sm font-bold transition-colors hover:text-[var(--ir-mint)]"
            >
              {SALES_EMAIL}
            </a>
            <div className="mt-4">
              <button
                type="button"
                onClick={openSupportChat}
                className="inline-flex min-h-11 items-center gap-3 rounded-full border border-white/20 bg-white/5 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:border-[var(--ir-mint)] hover:text-[var(--ir-mint)]"
              >
                <MessageCircle className="h-5 w-5" aria-hidden="true" />
                <span>{t('footer.chat')}</span>
              </button>
            </div>
          </div>

          <nav aria-label={t('footer.sections.segments')}>
            <h2 className={COLUMN_TITLE_CLASS}>
              {t('footer.sections.segments')}
            </h2>
            <ul>
              {segmentMarketingRoutes.map((route) => (
                <li key={route.segmentCode}>
                  <Link to={route.path} className={COLUMN_LINK_CLASS}>
                    {t(route.navLabelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label={t('footer.sections.product')}>
            <h2 className={COLUMN_TITLE_CLASS}>
              {t('footer.sections.product')}
            </h2>
            <ul>
              <li>
                <Link to="/precios" className={COLUMN_LINK_CLASS}>
                  {t('nav.pricing')}
                </Link>
              </li>
              <li>
                <Link to="/modulos" className={COLUMN_LINK_CLASS}>
                  {t('nav.modules')}
                </Link>
              </li>
              <li>
                <Link to="/ia" className={COLUMN_LINK_CLASS}>
                  {t('nav.ai')}
                </Link>
              </li>
              <li>
                <Link to="/comparar" className={COLUMN_LINK_CLASS}>
                  {t('nav.comparison')}
                </Link>
              </li>
            </ul>
          </nav>

          <nav aria-label={t('footer.sections.company')}>
            <h2 className={COLUMN_TITLE_CLASS}>
              {t('footer.sections.company')}
            </h2>
            <ul>
              <li>
                <Link
                  to={{ pathname: '/', hash: '#demo-form' }}
                  className={COLUMN_LINK_CLASS}
                >
                  {t('footer.contact')}
                </Link>
              </li>
              <li>
                <Link to="/login" className={COLUMN_LINK_CLASS}>
                  {t('nav.login')}
                </Link>
              </li>
            </ul>
          </nav>

          <nav aria-label={t('footer.sections.legal')}>
            <h2 className={COLUMN_TITLE_CLASS}>{t('footer.sections.legal')}</h2>
            <ul>
              <li>
                <Link to="/privacy" className={COLUMN_LINK_CLASS}>
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className={COLUMN_LINK_CLASS}>
                  {t('footer.terms')}
                </Link>
              </li>
            </ul>
          </nav>
        </div>

        <div className="mb-12 rounded-[1.5rem] border border-white/10 bg-white/[0.06] p-6">
          <h2 className="font-sans text-xs font-semibold uppercase tracking-[0.14em] text-[var(--ir-mint)]">
            {t('footer.legalIdentification')}
          </h2>
          <dl className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-[1fr_0.7fr_1.6fr_1fr]">
            <div>
              <dt className={LEGAL_TERM_CLASS}>{t('footer.legalName')}</dt>
              <dd className={LEGAL_VALUE_CLASS}>
                {CRYPTOLOGY_CONTACT.legalName}
              </dd>
            </div>
            <div>
              <dt className={LEGAL_TERM_CLASS}>{t('footer.ruc')}</dt>
              <dd className={LEGAL_VALUE_CLASS}>{CRYPTOLOGY_CONTACT.ruc}</dd>
            </div>
            <div>
              <dt className={LEGAL_TERM_CLASS}>
                {t('footer.registeredAddress')}
              </dt>
              <dd className={LEGAL_VALUE_CLASS}>
                {CRYPTOLOGY_CONTACT.address}
              </dd>
            </div>
            <div>
              <dt className={LEGAL_TERM_CLASS}>{t('footer.contactEmail')}</dt>
              <dd className={LEGAL_VALUE_CLASS}>
                <a
                  href={`mailto:${CRYPTOLOGY_CONTACT.email}`}
                  className="inline-block py-1 transition-colors hover:text-[var(--ir-mint)]"
                >
                  {CRYPTOLOGY_CONTACT.email}
                </a>
              </dd>
            </div>
          </dl>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row">
          <p className="text-xs text-white/65">{t('footer.copyright')}</p>
          <LanguageSwitcher reversed />
        </div>
      </div>
    </footer>
  );
}
